import React from 'react'
import { useTranslation } from 'react-i18next'
import { IconOpenTab } from 'components/Icon/IconOpenTab'
import { Button } from 'components/Button/Button'
import { MyDrawer } from 'components/MyDrawer'

export interface AuthRepPrintDrawerProps {
  onClose: () => void
}

const CMS10106DownloadLink = 'https://www.cms.gov/Medicare/CMS-Forms/CMS-Forms/Downloads/CMS10106.pdf'

interface ListItem1Props {
  text: string,
  linkText: string,
  // Until upgrading to v2 of events, keeping parity with existing, which always passes the text in english
  analyticsLinkText: string,
  href: string,
  openTabIcon: React.ReactNode
}

// Simple helpers components to use in the drawer
const Helpers = {
  Heading: ({ heading }: { heading: string }) => (
    <h3 className='ds-text-heading--xl my-u-mt--0 my-u-mb--12'>{heading}</h3>
  ),
  ListItem1: ({ text, linkText, href, openTabIcon, analyticsLinkText }: ListItem1Props) => (
    <li>
      {text}
      <Button
        target='_blank'
        rel='noopener'
        href={href}
        siteActivityProps={{
          component: 'Authorized Representative',
          clickData: {
            linkText: analyticsLinkText,
            linkUrl: href
          }
        }}
      >
        {linkText}
        {openTabIcon}
      </Button>
    </li>
  ),
  // Utility type so structure in JSX is uniform
  ListItemN: ({ children }: { children: React.ReactNode }) => (
    <li>{children}</li>
  )
}

export const AuthRepPrintDrawer: React.FC<AuthRepPrintDrawerProps> = ({ onClose }) => {
  const { t } = useTranslation('authRep')
  const openTabIcon = (
    <IconOpenTab
      className='ds-u-display--inline-block my-u-m--2'
      srText={t('drawer.openPageSrText')}
    />
  )

  return (
    <MyDrawer
      containerId='medicare-my-authrep-print-drawer'
      heading={t('drawer.heading1')}
      onClose={onClose}
      eventNamespace="authRep"
      openEventKey="authrep_printdrawer_opendrawer"
      closeEventKey="authrep_printdrawer_closedrawer"
    >
      <p>{t('drawer.paragraph')}</p>
      <Helpers.Heading heading={t('drawer.list1.heading2')} />
      <ul className='my-u-ul-spacing'>
        <Helpers.ListItem1
          text={t('drawer.list1.li1.text')}
          linkText={t('drawer.list1.li1.linkText')}
          analyticsLinkText={t('drawer.list1.li1.linkText', { lng: 'en' })}
          href={CMS10106DownloadLink}
          openTabIcon={openTabIcon}
        />
        <Helpers.ListItemN>{t('drawer.list1.li2')}</Helpers.ListItemN>
        <Helpers.ListItemN>{t('drawer.list1.li3')}</Helpers.ListItemN>
        <Helpers.ListItemN>{t('drawer.list1.li4')}</Helpers.ListItemN>
      </ul>
      <Helpers.Heading heading={t('drawer.list2.heading2')} />
      <ul className='my-u-ul-spacing my-u-mb--20 my-u-pb--16'>
        <Helpers.ListItem1
          text={t('drawer.list2.li1.text')}
          linkText={t('drawer.list2.li1.linkText')}
          analyticsLinkText={t('drawer.list2.li1.linkText', { lng: 'en' })}
          href={CMS10106DownloadLink}
          openTabIcon={openTabIcon}
        />
        <Helpers.ListItemN>{t('drawer.list2.li2')}</Helpers.ListItemN>
      </ul>
    </MyDrawer>
  )
}
